<script setup lang="ts">
import { createGlobe } from '@/lib/cobe'
import pandaCoordinates from '@/assets/data/home-globe-panda.json'
import { homeGlobeCoverage } from '@/utils/homeGlobeCoverage'

type Vector = { x: number; y: number; z: number }
type Projected = Vector & { visible: boolean }

const { locale } = useI18n()
const colorMode = useColorMode()
const reducedMotion = ref(false)
const staticGlobe = ref(false)
const canvasRef = useTemplateRef<HTMLCanvasElement>('canvas')
const globeRef = useTemplateRef<HTMLCanvasElement>('globe')
let globeRenderer: ReturnType<typeof createGlobe> | null = null
const rootRef = useTemplateRef<HTMLElement>('root')
const radius = 205
const center = 300
const axisRoll = (15 * Math.PI) / 180
const sinRoll = Math.sin(axisRoll)
const cosRoll = Math.cos(axisRoll)
let tilt = (18 * Math.PI) / 180
let rotation = (-105 * Math.PI) / 180
let sinTilt = Math.sin(tilt)
let cosTilt = Math.cos(tilt)
const dragging = ref(false)
let pointerId: number | null = null
let pointerX = 0
let pointerY = 0
let longitudeVelocity = 0
let latitudeVelocity = 0
const activeLocation = ref<number | null>(null)
const tooltipPosition = ref({ x: 50, y: 50 })
let pointerMoved = false
let pointerStartX = 0
let pointerStartY = 0
let dragMovedAt = 0
const activeLocationName = computed(() => {
  if (activeLocation.value === null) return ''
  const location = homeGlobeCoverage[activeLocation.value]!
  return locale.value === 'zh' ? location.zh : location.en
})

function spherePoint(longitude: number, latitude: number): Vector {
  const lon = (longitude * Math.PI) / 180
  const lat = (latitude * Math.PI) / 180
  return { x: Math.cos(lat) * Math.sin(lon), y: Math.sin(lat), z: Math.cos(lat) * Math.cos(lon) }
}

const panda: Vector[] = []
// The original logo's silhouette replaces only Antarctica and rotates on the sphere.
const pandaOrientation = (105 * Math.PI) / 180
const pandaSin = Math.sin(pandaOrientation)
const pandaCos = Math.cos(pandaOrientation)
for (const [x, y] of pandaCoordinates) {
  panda.push({
    x: x! * pandaCos + y! * pandaSin,
    y: -Math.sqrt(1 - x! * x! - y! * y!),
    z: y! * pandaCos - x! * pandaSin,
  })
}

const hubs = homeGlobeCoverage.map((location) => spherePoint(location.longitude, location.latitude))
// The four mainland origins lead to global coverage; routes are illustrative, not live topology.
const originIds = ['shanghai', 'hangzhou', 'beijing', 'guangzhou']
const originIndices = originIds.map((id) => {
  const index = homeGlobeCoverage.findIndex((location) => location.id === id)
  if (index < 0) throw new Error(`Unknown globe origin: ${id}`)
  return index
})
const origins = new Set(originIndices)
const destinationIndices = homeGlobeCoverage.map((_, index) => index).filter((index) => !origins.has(index))
const pulseLocationIndices = destinationIndices.filter((index) => !['hong-kong', 'taipei'].includes(homeGlobeCoverage[index]!.id))
const connections = destinationIndices.map((index, routeIndex) => [originIndices[routeIndex % originIndices.length]!, index] as const)
const launchInterval = 2.4
const travelDuration = 3.6
// Match the existing InfrastructureSvg network's palette.
const flowColors = [
  { dark: '126, 211, 33', light: '45, 90, 39' },
  { dark: '0, 212, 179', light: '0, 135, 116' },
  { dark: '84, 143, 244', light: '42, 100, 196' },
  { dark: '245, 158, 11', light: '155, 100, 5' },
  { dark: '206, 87, 95', light: '176, 75, 80' },
]
const routes = connections.map(([from, to]) => {
  const start = hubs[from!]!
  const end = hubs[to!]!
  const angle = Math.acos(Math.max(-1, Math.min(1, start.x * end.x + start.y * end.y + start.z * end.z)))
  const sinAngle = Math.sin(angle)
  const points = Array.from({ length: 81 }, (_, step) => {
    const t = step / 80
    const a = Math.sin((1 - t) * angle) / sinAngle
    const b = Math.sin(t * angle) / sinAngle
    const lift = 1 + Math.sin(Math.PI * t) * (0.17 + angle * 0.11)
    return { x: (start.x * a + end.x * b) * lift, y: (start.y * a + end.y * b) * lift, z: (start.z * a + end.z * b) * lift }
  })
  return { projected: points.map(() => ({ x: 0, y: 0, z: 0, visible: false })), points }
})

type Packet = { routeIndex: number; launchedAt: number }
let packets: Packet[] = []
let nextLaunchAt = -2 * launchInterval

function advancePackets(time: number) {
  while (nextLaunchAt <= time) {
    packets = packets.filter((packet) => nextLaunchAt - packet.launchedAt <= travelDuration * 1.18)
    // Keep concurrent destinations distinct while choosing a fresh random route at each launch.
    const available = routes.map((_, index) => index).filter((index) => !packets.some((packet) => packet.routeIndex === index))
    const routeIndex = available[Math.floor(Math.random() * available.length)]!
    packets.push({ routeIndex, launchedAt: nextLaunchAt })
    nextLaunchAt += launchInterval
  }
  packets = packets.filter((packet) => time - packet.launchedAt <= travelDuration * 1.18)
}

let context: CanvasRenderingContext2D | null = null
let resizeObserver: ResizeObserver | null = null
let intersectionObserver: IntersectionObserver | null = null
let motionQuery: MediaQueryList | null = null
let frameId = 0
let frameTimer: ReturnType<typeof setTimeout> | null = null
let lastFrame = 0
let elapsed = 0
let inView = true
let dark = true
const nodePulseDuration = 0.9
let nodePulse: { index: number; startedAt: number } | null = null
let nextNodePulseAt = 1 + Math.random() * 2

function advanceNodePulse(time: number) {
  if (time < nextNodePulseAt) return
  nextNodePulseAt = time + 1 + Math.random() * 2
  const sinRotation = Math.sin(rotation)
  const cosRotation = Math.cos(rotation)
  // Choose a front-facing node so the occasional pulse is visible as the globe turns.
  const available = pulseLocationIndices.filter((index) => index !== nodePulse?.index && project(hubs[index]!, sinRotation, cosRotation).z > 0.15)
  if (!available.length) return
  nodePulse = { index: available[Math.floor(Math.random() * available.length)]!, startedAt: time }
}

function project(point: Vector, sinRotation: number, cosRotation: number, output?: Projected): Projected {
  const x = point.x * cosRotation + point.z * sinRotation
  const depth = point.z * cosRotation - point.x * sinRotation
  const y = point.y * cosTilt - depth * sinTilt
  const z = point.y * sinTilt + depth * cosTilt
  // Elevated arcs can remain visible beyond the limb even when their depth is negative.
  const result = output ?? { x: 0, y: 0, z: 0, visible: false }
  result.x = center + (x * cosRoll + y * sinRoll) * radius
  result.y = center + (x * sinRoll - y * cosRoll) * radius
  result.z = z
  result.visible = z >= 0 || x * x + y * y > 1.015
  return result
}

let atmosphere: CanvasGradient | null = null
let glowSprite: HTMLCanvasElement | null = null
let paintDark: boolean | null = null

function preparePaint(ctx: CanvasRenderingContext2D, green: string) {
  if (paintDark === dark) return
  paintDark = dark
  atmosphere = ctx.createRadialGradient(center, center, radius * 0.72, center, center, radius * 1.1)
  atmosphere.addColorStop(0, `rgba(${green}, 0)`)
  atmosphere.addColorStop(0.75, `rgba(${green}, ${dark ? 0.035 : 0.018})`)
  atmosphere.addColorStop(1, `rgba(${green}, 0)`)
  // Reuse a small glow texture instead of blurring every beam segment and city every frame.
  glowSprite = document.createElement('canvas')
  glowSprite.width = glowSprite.height = 64
  const glow = glowSprite.getContext('2d')!
  const gradient = glow.createRadialGradient(32, 32, 0, 32, 32, 32)
  gradient.addColorStop(0, `rgba(${green}, ${dark ? 0.6 : 0.25})`)
  gradient.addColorStop(0.25, `rgba(${green}, ${dark ? 0.25 : 0.1})`)
  gradient.addColorStop(1, `rgba(${green}, 0)`)
  glow.fillStyle = gradient
  glow.fillRect(0, 0, 64, 64)
}

function drawRouteFlow(ctx: CanvasRenderingContext2D, points: Projected[], routeIndex: number) {
  // Keep a faint moving dotted route; brighter traffic is limited by the packet scheduler.
  const palette = flowColors[routeIndex % flowColors.length]!
  const color = dark ? palette.dark : palette.light
  ctx.lineCap = 'round'
  ctx.setLineDash([0.8, 2.4])
  ctx.lineDashOffset = -elapsed * (3.2 + (routeIndex % 3) * 0.4)
  ctx.lineWidth = 0.65
  ctx.strokeStyle = `rgba(${color}, ${dark ? 0.22 : 0.24})`
  ctx.beginPath()
  let connected = false
  for (const point of points) {
    if (!point.visible) {
      connected = false
      continue
    }
    if (connected) ctx.lineTo(point.x, point.y)
    else ctx.moveTo(point.x, point.y)
    connected = true
  }
  ctx.stroke()
  ctx.setLineDash([])
  ctx.lineDashOffset = 0
  ctx.lineCap = 'butt'
}

function draw() {
  if (!context) return
  const ctx = context
  const sinRotation = Math.sin(rotation)
  const cosRotation = Math.cos(rotation)
  const green = dark ? '126, 211, 33' : '45, 90, 39'
  const ink = dark ? '220, 237, 223' : '61, 93, 72'
  ctx.clearRect(0, 0, 600, 600)

  preparePaint(ctx, green)
  ctx.fillStyle = atmosphere!
  ctx.fillRect(65, 65, 470, 470)
  globeRenderer?.update({
    rotation,
    tilt,
    radius,
    homepage: true,
    dark,
    ink: dark ? [220 / 255, 237 / 255, 223 / 255] : [61 / 255, 93 / 255, 72 / 255],
    green: dark ? [126 / 255, 211 / 255, 33 / 255] : [45 / 255, 90 / 255, 39 / 255],
  })

  for (const [routeIndex, route] of routes.entries()) {
    const packet = packets.find((packet) => packet.routeIndex === routeIndex)
    const points = route.projected
    route.points.forEach((point, index) => project(point, sinRotation, cosRotation, points[index]))
    drawRouteFlow(ctx, points, routeIndex)
    if (!packet) continue
    const age = elapsed - packet.launchedAt
    const progress = age / travelDuration

    const source = points[0]!
    const departure = age / 0.65
    if (departure < 1 && source.z > 0) {
      ctx.beginPath()
      ctx.arc(source.x, source.y, 4 + departure * 6, 0, Math.PI * 2)
      ctx.strokeStyle = `rgba(${green}, ${(1 - departure) * 0.35})`
      ctx.lineWidth = 1.2
      ctx.stroke()
    }
    const head = Math.floor(progress * 80)
    for (let tail = 0; tail < 9; tail += 3) {
      ctx.beginPath()
      for (let segment = tail; segment < tail + 3; segment++) {
        const step = head - segment
        if (step < 1 || step > 80) continue
        const point = points[step]!
        if (!point.visible) continue
        const size = 1.05 - segment * 0.035
        ctx.moveTo(point.x + size, point.y)
        ctx.arc(point.x, point.y, size, 0, Math.PI * 2)
      }
      ctx.fillStyle = `rgba(${green}, ${(1 - (tail + 1) / 9) * 0.65})`
      ctx.fill()
    }
    if (head >= 0 && head <= 80) {
      const tip = points[head]!
      if (tip.visible) {
        ctx.drawImage(glowSprite!, tip.x - 8, tip.y - 8, 16, 16)
        ctx.beginPath()
        ctx.arc(tip.x, tip.y, 1.6, 0, Math.PI * 2)
        ctx.fillStyle = dark ? 'rgba(236, 255, 215, 0.98)' : `rgba(${green}, 0.95)`
        ctx.fill()
      }
    }
    const arrival = (progress - 0.94) / 0.24
    const destination = points[80]!
    if (arrival > 0 && arrival < 1 && destination.z > 0) {
      ctx.beginPath()
      ctx.arc(destination.x, destination.y, 3 + arrival * 7, 0, Math.PI * 2)
      ctx.strokeStyle = `rgba(${green}, ${(1 - arrival) * 0.35})`
      ctx.lineWidth = 1.2
      ctx.stroke()
    }
  }

  hubs.forEach((point, index) => {
    const p = project(point, sinRotation, cosRotation)
    if (p.z <= 0.02) return
    const isOrigin = origins.has(index)
    const nodeColor = isOrigin ? green : ink
    ctx.beginPath()
    ctx.arc(p.x, p.y, activeLocation.value === index ? 9 : isOrigin ? 7 : 4, 0, Math.PI * 2)
    ctx.strokeStyle = `rgba(${nodeColor}, ${(isOrigin ? 0.4 : 0.18) * p.z})`
    ctx.lineWidth = 1
    ctx.stroke()
    if (isOrigin) ctx.drawImage(glowSprite!, p.x - 12, p.y - 12, 24, 24)
    ctx.beginPath()
    ctx.arc(p.x, p.y, activeLocation.value === index ? 4 : isOrigin ? 3.5 : 2, 0, Math.PI * 2)
    ctx.fillStyle = `rgba(${nodeColor}, ${isOrigin ? 0.75 + p.z * 0.25 : 0.35 + p.z * 0.5})`
    ctx.fill()
    if (!reducedMotion.value && nodePulse?.index === index) {
      const progress = (elapsed - nodePulse.startedAt) / nodePulseDuration
      if (progress >= 0 && progress < 1) {
        const intensity = Math.sin(progress * Math.PI) * Math.min(1, p.z * 3)
        ctx.globalAlpha = intensity
        ctx.drawImage(glowSprite!, p.x - 16, p.y - 16, 32, 32)
        ctx.beginPath()
        ctx.arc(p.x, p.y, 4 + progress * 9, 0, Math.PI * 2)
        ctx.strokeStyle = `rgba(${green}, ${(1 - progress) * 0.55})`
        ctx.lineWidth = 1
        ctx.stroke()
        ctx.beginPath()
        ctx.arc(p.x, p.y, 2 + intensity * 1.5, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(${green}, 0.9)`
        ctx.fill()
        ctx.globalAlpha = 1
      }
    }
    if (activeLocation.value === index) {
      const x = Math.max(18, Math.min(82, p.x / 6))
      const y = p.y / 6
      if (tooltipPosition.value.x !== x || tooltipPosition.value.y !== y) tooltipPosition.value = { x, y }
    }
  })
}

function screenToGlobe(deltaX: number, deltaY: number): [number, number] {
  // Undo the visual axis roll so dragging and arrow keys follow screen directions.
  return [deltaX * cosRoll + deltaY * sinRoll, -deltaX * sinRoll + deltaY * cosRoll]
}

function moveGlobe(deltaX: number, deltaY: number) {
  rotation += deltaX
  tilt = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, tilt + deltaY))
  sinTilt = Math.sin(tilt)
  cosTilt = Math.cos(tilt)
  requestDraw()
}

function pickLocation(clientX: number, clientY: number) {
  const canvas = canvasRef.value
  if (!canvas) return
  const bounds = canvas.getBoundingClientRect()
  const x = ((clientX - bounds.left) * 600) / bounds.width
  const y = ((clientY - bounds.top) * 600) / bounds.height
  // Use a screen-sized hit target so small screens remain touchable.
  let closestDistance = Math.pow((14 * 600) / bounds.width, 2)
  let closest: number | null = null
  hubs.forEach((point, index) => {
    const projected = project(point, Math.sin(rotation), Math.cos(rotation))
    if (projected.z <= 0.02) return
    const distance = (projected.x - x) ** 2 + (projected.y - y) ** 2
    if (distance < closestDistance) {
      closestDistance = distance
      closest = index
    }
  })
  if (activeLocation.value !== closest) {
    activeLocation.value = closest
    requestDraw()
  }
}

function startDrag(event: PointerEvent) {
  if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return
  const canvas = canvasRef.value
  if (!canvas) return
  pointerId = event.pointerId
  pointerX = pointerStartX = event.clientX
  pointerY = pointerStartY = event.clientY
  pointerMoved = false
  dragMovedAt = performance.now()
  longitudeVelocity = 0
  latitudeVelocity = 0
  dragging.value = true
  activeLocation.value = null
  canvas.setPointerCapture(event.pointerId)
  syncAnimation()
}

function drag(event: PointerEvent) {
  // Leaving transitions can still deliver pointer events after Vue clears the ref.
  const canvas = canvasRef.value
  if (!canvas) return
  if (pointerId === null) {
    if (event.pointerType === 'mouse') pickLocation(event.clientX, event.clientY)
    return
  }
  if (event.pointerId !== pointerId) return
  pointerMoved ||= Math.hypot(event.clientX - pointerStartX, event.clientY - pointerStartY) > 5
  const scale = Math.PI / canvas.getBoundingClientRect().width
  const [dx, dy] = screenToGlobe((event.clientX - pointerX) * scale, (event.clientY - pointerY) * scale)
  pointerX = event.clientX
  pointerY = event.clientY
  dragMovedAt = performance.now()
  longitudeVelocity = Math.max(-1.5, Math.min(1.5, dx * 12))
  latitudeVelocity = Math.max(-1, Math.min(1, dy * 12))
  moveGlobe(dx, dy)
}

function stopDrag(event: PointerEvent) {
  if (event.pointerId !== pointerId) return
  pointerId = null
  dragging.value = false
  if (canvasRef.value?.hasPointerCapture(event.pointerId)) canvasRef.value.releasePointerCapture(event.pointerId)
  if (event.type !== 'pointerup' || performance.now() - dragMovedAt > 100) {
    longitudeVelocity = 0
    latitudeVelocity = 0
  }
  if (!pointerMoved && event.type === 'pointerup') pickLocation(event.clientX, event.clientY)
}

function clearHover(event: PointerEvent | FocusEvent) {
  if (event.type === 'pointerleave' && (event as PointerEvent).pointerType === 'touch') return
  if (pointerId === null) {
    activeLocation.value = null
    draw()
  }
}

function rotateWithKeyboard(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    activeLocation.value = null
    draw()
    return
  }
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    const nextIndex = ((activeLocation.value ?? -1) + 1) % hubs.length
    activeLocation.value = nextIndex
    const location = homeGlobeCoverage[nextIndex]!
    rotation = (-location.longitude * Math.PI) / 180
    tilt = (location.latitude * Math.PI) / 180
    sinTilt = Math.sin(tilt)
    cosTilt = Math.cos(tilt)
    draw()
    return
  }
  const directions: Record<string, [number, number]> = {
    ArrowLeft: [-0.15, 0],
    ArrowRight: [0.15, 0],
    ArrowUp: [0, -0.12],
    ArrowDown: [0, 0.12],
  }
  const direction = directions[event.key]
  if (!direction) return
  event.preventDefault()
  activeLocation.value = null
  longitudeVelocity = 0
  latitudeVelocity = 0
  moveGlobe(...screenToGlobe(...direction))
}

function shouldAnimate() {
  return !reducedMotion.value && inView && !document.hidden
}

function cancelFrame() {
  cancelAnimationFrame(frameId)
  if (frameTimer !== null) clearTimeout(frameTimer)
  frameId = 0
  frameTimer = null
}

function scheduleFrame() {
  // Automatic motion needs 30 fps; dragging uses 60 fps. Sleeping between frames
  // also avoids waking at the display's full 120/144 Hz refresh rate.
  const interval = 1000 / (dragging.value ? 60 : 30)
  const delay = Math.max(0, interval - (performance.now() - lastFrame) - 4)
  frameTimer = setTimeout(() => {
    frameTimer = null
    frameId = requestAnimationFrame(tick)
  }, delay)
}

function requestDraw() {
  if (!shouldAnimate()) draw()
  else if (!frameId && frameTimer === null) frameId = requestAnimationFrame(tick)
}

function tick() {
  frameId = 0
  if (!shouldAnimate()) return
  const now = performance.now()
  const interval = 1000 / (dragging.value ? 60 : 30)
  if (lastFrame && now - lastFrame < interval - 0.75) {
    scheduleFrame()
    return
  }
  const dt = lastFrame ? Math.min((now - lastFrame) / 1000, 0.05) : 0
  elapsed += dt
  advancePackets(elapsed)
  if (!dragging.value && activeLocation.value === null) {
    rotation += (0.055 + longitudeVelocity) * dt
    tilt = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, tilt + latitudeVelocity * dt))
    const damping = Math.exp(-5 * dt)
    longitudeVelocity *= damping
    latitudeVelocity *= damping
    sinTilt = Math.sin(tilt)
    cosTilt = Math.cos(tilt)
  }
  lastFrame = now
  advanceNodePulse(elapsed)
  draw()
  scheduleFrame()
}

function syncAnimation() {
  cancelFrame()
  lastFrame = 0
  draw()
  if (context && shouldAnimate()) frameId = requestAnimationFrame(tick)
}

function resizeCanvas() {
  const canvas = canvasRef.value
  if (!canvas || !context) return
  const width = canvas.getBoundingClientRect().width
  globeRenderer?.resize(width, Math.min(window.devicePixelRatio, 2))
  const size = Math.round(width * Math.min(window.devicePixelRatio, 2))
  if (size <= 0 || (canvas.width === size && canvas.height === size)) return
  paintDark = null
  canvas.width = size
  canvas.height = size
  context.setTransform(size / 600, 0, 0, size / 600, 0, 0)
  requestDraw()
}

function onMotionChange(event: MediaQueryListEvent) {
  reducedMotion.value = event.matches
  syncAnimation()
}

watch(
  () => colorMode.value,
  (value) => {
    dark = value === 'dark'
    draw()
  },
)

onMounted(() => {
  advancePackets(elapsed)
  try {
    globeRenderer = createGlobe(globeRef.value!, panda)
  } catch (error) {
    // An optional homepage illustration must also work when WebGL is unavailable.
    if (!(error instanceof Error) || error.message !== 'cobe: WebGL is unavailable') throw error
    staticGlobe.value = true
    return
  }
  context = canvasRef.value!.getContext('2d')
  if (!context) throw new Error('Homepage globe requires a 2D canvas context')
  dark = colorMode.value === 'dark'
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  reducedMotion.value = motionQuery.matches
  motionQuery.addEventListener('change', onMotionChange)
  resizeObserver = new ResizeObserver(resizeCanvas)
  resizeObserver.observe(canvasRef.value!)
  intersectionObserver = new IntersectionObserver(([entry]) => {
    inView = entry!.isIntersecting
    syncAnimation()
  })
  intersectionObserver.observe(rootRef.value!)
  document.addEventListener('visibilitychange', syncAnimation)
  resizeCanvas()
  syncAnimation()
})

onBeforeUnmount(() => {
  pointerId = null
  dragging.value = false
  cancelFrame()
  resizeObserver?.disconnect()
  intersectionObserver?.disconnect()
  motionQuery?.removeEventListener('change', onMotionChange)
  document.removeEventListener('visibilitychange', syncAnimation)
  context = null
  globeRenderer?.destroy()
  globeRenderer = null
})
</script>

<template lang="pug">
.connection-globe(ref="root" :class="{ 'is-dragging': dragging }")
  img.globe-fallback(
    v-if="staticGlobe"
    :src="`/img/index/home-globe-${colorMode.value === 'dark' ? 'dark' : 'light'}.svg`"
    width="600"
    height="600"
    draggable="false"
    :alt="locale === 'zh' ? 'PandaFan 全球网络覆盖' : 'PandaFan global network coverage'"
  )
  canvas.globe-surface(v-if="!staticGlobe" ref="globe" aria-hidden="true")
  canvas(
    v-if="!staticGlobe"
    ref="canvas"
    width="600"
    height="600"
    tabindex="0"
    role="group"
    :aria-describedby="activeLocation !== null ? 'globe-location-tooltip' : undefined"
    :aria-label="locale === 'zh' ? '互动覆盖地球：拖动或方向键旋转，回车查看覆盖城市与地区，Esc 关闭提示' : 'Coverage globe: drag or use arrow keys to rotate. Enter explores locations. Escape dismisses details.'"
    @pointerdown="startDrag"
    @pointermove="drag"
    @pointerup="stopDrag"
    @pointerleave="clearHover"
    @blur="clearHover"
    @pointercancel="stopDrag"
    @lostpointercapture="stopDrag"
    @keydown="rotateWithKeyboard"
  )
  #globe-location-tooltip.globe-tooltip(
    v-if="activeLocation !== null"
    role="tooltip"
    :style="{ left: `clamp(90px, ${tooltipPosition.x}%, calc(100% - 90px))`, top: `${tooltipPosition.y}%` }"
  )
    strong {{ activeLocationName }}
  span.sr-only(aria-live="polite") {{ activeLocationName }}
</template>

<style scoped>
.connection-globe {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
}
.globe-fallback {
  display: block;
  width: 100%;
  height: 100%;
}
canvas {
  display: block;
  width: 100%;
  height: 100%;
  cursor: grab;
  touch-action: none;
}
.globe-surface {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
canvas:not(.globe-surface) {
  position: relative;
}
.is-dragging canvas {
  cursor: grabbing;
}
canvas:focus-visible {
  outline: 2px solid var(--home-green);
  outline-offset: 4px;
  border-radius: 50%;
}
.globe-tooltip {
  position: absolute;
  z-index: 2;
  transform: translate(-50%, calc(-100% - 12px));
  pointer-events: none;
  width: max-content;
  max-width: 180px;
  box-sizing: border-box;
  padding: 5px 10px;
  border: 1px solid var(--home-border);
  border-radius: 999px;
  background: color-mix(in srgb, var(--home-paper) 70%, transparent);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: var(--home-text);
  box-shadow: 0 2px 8px #0001;
}
.globe-tooltip strong {
  display: block;
  overflow: hidden;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
